import type { LobbyMemberInfo } from '../../api/types';

export const SLOTS_PER_SIDE = 5;

export type BoardSlot = {
  /** 1-based. */
  slot: number;
  member: LobbyMemberInfo | null;
};

export type LobbyBoard = {
  radiant: BoardSlot[];
  dire: BoardSlot[];
  /** Everyone not in a player slot: unassigned, spectators, broadcasters, coaches, the bot. */
  bench: LobbyMemberInfo[];
  bot: LobbyMemberInfo | null;
  /** Seated players across both sides. The hosting bot is not a player, even in a slot. */
  seated: number;
};

function emptySide(): BoardSlot[] {
  return Array.from({ length: SLOTS_PER_SIDE }, (_, index) => ({ slot: index + 1, member: null }));
}

/**
 * Lays the members out the way the Dota lobby does: five slots per side, and
 * a bench for everyone else. A member whose slot is out of range, or a second
 * member claiming the same slot, lands on the bench rather than vanishing.
 */
export function buildLobbyBoard(members: LobbyMemberInfo[] | undefined): LobbyBoard {
  const board: LobbyBoard = { radiant: emptySide(), dire: emptySide(), bench: [], bot: null, seated: 0 };
  for (const member of members ?? []) {
    if (member.is_bot) board.bot = member;
    const side = member.team === 'radiant' ? board.radiant : member.team === 'dire' ? board.dire : null;
    const target = side?.[member.slot - 1];
    if (side && target && target.member === null) {
      target.member = member;
      if (!member.is_bot) board.seated += 1;
    } else {
      board.bench.push(member);
    }
  }
  return board;
}

/**
 * How many players a side holds. The hosting bot occupies a slot only so it can
 * run the lobby, so it is never counted as one of them.
 */
export function seatedPlayers(slots: BoardSlot[]): number {
  return slots.filter((slot) => slot.member !== null && !slot.member.is_bot).length;
}

const TEAM_LABELS: Record<LobbyMemberInfo['team'], string> = {
  radiant: 'Radiant',
  dire: 'Dire',
  spectator: 'Spectator',
  broadcaster: 'Broadcaster',
  player_pool: 'Unassigned',
  no_team: 'No team',
  other: 'Other',
};

/** Human name of a lobby team, as the seating board and the movement log use it. */
export function teamLabel(team: LobbyMemberInfo['team']): string {
  return TEAM_LABELS[team] ?? 'Other';
}

/** What a bench row says about where the member is. */
export function benchLabel(member: LobbyMemberInfo): string {
  if (member.coach_team) return `${teamLabel(member.coach_team)} coach`;
  return teamLabel(member.team);
}

/** The bot never kicks itself; use leave or destroy for that. */
export function canKick(member: LobbyMemberInfo): boolean {
  return !member.is_bot;
}

/** Unseat only makes sense for someone actually in a player slot. */
export function canUnseat(member: LobbyMemberInfo): boolean {
  return !member.is_bot && (member.team === 'radiant' || member.team === 'dire');
}
